// Report periods: ranges of East Africa days (docs/plans/phase1-reports.md §3.2, P8).
//
// Every report filters on event time with a half-open range of UTC instants that starts
// at EAT midnight on the first day and ends at EAT midnight after the last day. No report
// uses a UTC date or the server's time zone.

import type { Result } from '../result.ts';
import { err, ok } from '../result.ts';
import type { CalendarDay, InstantRange } from '../time.ts';
import {
  addDays,
  compareDays,
  daysBetween,
  daysEqual,
  formatDay,
  formatIsoDay,
  isValidDay,
  startOfEatDay,
} from '../time.ts';

/** Both days are included: 22 Sep to 22 Sep is one day. */
export interface DayPeriod {
  readonly from: CalendarDay;
  readonly to: CalendarDay;
}

/** Plan P8: registers list patients, so they are limited to a month; money to a year. */
export const REPORT_PERIOD_LIMIT_DAYS = { register: 31, money: 366 } as const;

export type ReportPeriodErrorCode = 'report.period_invalid' | 'report.period_too_long';

/** English descriptions; the API and the web app own the wording shown to a user. */
export const REPORT_PERIOD_ERRORS: Record<ReportPeriodErrorCode, string> = {
  'report.period_invalid': 'The period must start on or before the day it ends',
  'report.period_too_long': 'The period is longer than this report allows',
};

/** The number of days in the period, or why it can't be used. */
export function checkDayPeriod(
  period: DayPeriod,
  maxDays: number,
): Result<{ days: number }, ReportPeriodErrorCode> {
  if (!isValidDay(period.from) || !isValidDay(period.to)) return err('report.period_invalid');
  if (compareDays(period.from, period.to) > 0) return err('report.period_invalid');
  const days = daysBetween(period.from, period.to) + 1;
  if (days > maxDays) return err('report.period_too_long');
  return ok({ days });
}

/** `[EAT midnight on from, EAT midnight after to)` as UTC instants. */
export function eatPeriodRange(period: DayPeriod): InstantRange {
  return {
    startInclusive: startOfEatDay(period.from),
    endExclusive: startOfEatDay(addDays(period.to, 1)),
  };
}

/** `202609`: the period key KHIS uses for a monthly return. */
export function periodKey(year: number, month: number): string {
  if (!Number.isInteger(month) || month < 1 || month > 12)
    throw new RangeError(`Month out of range: ${month}`);
  return `${String(year).padStart(4, '0')}${String(month).padStart(2, '0')}`;
}

/** `22 Sep 2026`, or `01 Sep 2026 to 30 Sep 2026`. */
export function formatPeriod(period: DayPeriod): string {
  if (daysEqual(period.from, period.to)) return formatDay(period.from);
  return `${formatDay(period.from)} to ${formatDay(period.to)}`;
}

/** `2026-09-22`, or `2026-09-01_2026-09-30`, for file names. */
export function isoPeriod(period: DayPeriod): string {
  if (daysEqual(period.from, period.to)) return formatIsoDay(period.from);
  return `${formatIsoDay(period.from)}_${formatIsoDay(period.to)}`;
}
